import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Plus, FileText, Loader2, Trash2, ArrowRight, LogOut, Home, BookOpen, Sparkles } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { authStorage } from '@/utils/authStorage';
import { thesisApi } from '@/api/thesisApi';
import { ThemeToggle } from '@/components/ThemeToggle';
import AppFooter from '@/components/AppFooter';
import LegalDisclaimer from '@/components/LegalDisclaimer';
import RUOBadge from '@/components/RUOBadge';
import InkPhDLogo from '@/components/InkPhDLogo';
import ProjectCreationWizard from '@/components/ProjectCreationWizard';
import PlanBadge from '@/components/PlanBadge';
import UserMenu from '@/components/UserMenu';
import TrialBanner from '@/components/TrialBanner';

const PROJECT_TYPE_LABEL = {
  dissertation: 'Dissertation',
  research_article: 'Research Article',
  systematic_review: 'Systematic Review',
  meta_analysis: 'Meta-analysis',
  clinical_protocol: 'Clinical Protocol',
  grant_proposal: 'Grant Proposal',
  book: 'Book',
  case_report: 'Case Report',
  conference_abstract: 'Conference Abstract',
  editorial: 'Editorial',
};

export default function ThesisProjects() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [demoBusy, setDemoBusy] = useState(false);

  const openDemo = async () => {
    setDemoBusy(true);
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/thesis/demo-project`, null,
        { params: { user_id: user.id } });
      toast.success('Demo project ready — 8 sources, 2 finished sections');
      navigate(`/thesis/${data.project_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not open the demo project');
    } finally {
      setDemoBusy(false);
    }
  };

  useEffect(() => {
    if (!authStorage.isAuthorAuthenticated()) {
      navigate('/thesis/login');
      return;
    }
    setUser(authStorage.getAuthorUser());
  }, [navigate]);

  const load = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const data = await thesisApi.listProjects(user.id);
      setProjects(data.projects || []);
    } catch (e) {
      toast.error('Could not load projects: ' + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { if (user) load(); }, [user, load]);

  const handleDelete = async (projectId, title) => {
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
    try {
      await thesisApi.deleteProject(user.id, projectId);
      toast.success('Project deleted');
      load();
    } catch (e) {
      toast.error('Delete failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  const handleLogout = () => {
    authStorage.clearAuthorAuth();
    navigate('/thesis/login');
  };

  const handleCreated = (project) => {
    navigate(`/thesis/${project.id}`);
  };

  if (!user) return null;

  return (
    <div className="min-h-screen flex flex-col bg-white dark:bg-[#0a0e1a] text-foreground thesis-brand">
      <LegalDisclaimer variant="banner" lang="en" />
      <header className="border-b border-border bg-card/80 backdrop-blur sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-3 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-2">
          <Link to="/thesis" className="flex items-center gap-3" data-testid="thesis-home-link">
            <InkPhDLogo size="md" />
            <RUOBadge />
            <div className="text-[10px] text-muted-foreground uppercase tracking-widest hidden md:inline">
              Scientific Writing Workspace
            </div>
          </Link>
          <div className="flex items-center gap-2 sm:gap-3">
            <PlanBadge />
            <Button variant="ghost" size="sm" asChild data-testid="thesis-guide-link">
              <Link to="/guides" title="Guides — article & dissertation">
                <BookOpen className="w-4 h-4 sm:mr-2" /><span className="hidden sm:inline">Guide</span>
              </Link>
            </Button>
            <Button variant="ghost" size="sm" asChild data-testid="thesis-landing-link">
              <Link to="/" title="Open the public site — your session stays active">
                <Home className="w-4 h-4 sm:mr-2" /><span className="hidden sm:inline">Home page</span>
              </Link>
            </Button>
            <ThemeToggle />
            <Button variant="ghost" size="sm" onClick={handleLogout}
                    data-testid="thesis-logout-btn" className="hidden lg:inline-flex">
              <LogOut className="w-4 h-4 mr-2" />Sign out
            </Button>
            <UserMenu />
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-3 sm:px-6 py-6 sm:py-12 flex-1 w-full">
        <TrialBanner />
        <div className="flex items-start justify-between mb-10 gap-4">
          <div>
            <h1 className="text-4xl sm:text-5xl font-serif font-semibold mb-3 tracking-tight"
                style={{ fontFamily: '"Fraunces", Georgia, serif' }}>
              Your <span className="text-[#1d4ed8] dark:text-[#7aa9ff]">research projects</span>
            </h1>
            <p className="text-muted-foreground text-base max-w-xl">
              Evidence-grounded academic writing across dissertations, journal manuscripts,
              systematic reviews, protocols, and grant proposals.
            </p>
          </div>
          <div className="flex flex-wrap gap-2 shrink-0">
            {!projects.some((p) => p.is_demo) && (
              <Button variant="outline" disabled={demoBusy} onClick={openDemo}
                data-testid="open-demo-project-btn"
                title="A ready project with 8 indexed sources and two finished sections"
                className="gap-2 rounded-full border-violet-500/50 text-violet-700 dark:text-violet-300">
                {demoBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                See a demo project
              </Button>
            )}
            <Button onClick={() => setWizardOpen(true)} data-testid="create-thesis-project-btn"
                    className="gap-2 bg-[#0b1a35] hover:bg-[#1d4ed8] text-white rounded-full">
              <Plus className="w-4 h-4" /> New project
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : projects.length === 0 ? (
          <Card className="text-center py-16 border-dashed border-2" data-testid="thesis-empty-state">
            <CardContent>
              <div className="w-16 h-16 rounded-2xl bg-[#1d4ed8]/10 dark:bg-[#1d4ed8]/20 flex items-center justify-center mx-auto mb-5">
                <FileText className="w-8 h-8 text-[#1d4ed8] dark:text-[#7aa9ff]" />
              </div>
              <p className="text-xl font-serif font-semibold mb-2"
                 style={{ fontFamily: '"Fraunces", Georgia, serif' }}>
                Start your first scientific writing project
              </p>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                Build a private evidence library, retrieve PubMed literature, and draft scientifically defensible manuscripts.
              </p>
              <Button onClick={() => setWizardOpen(true)} className="bg-[#0b1a35] hover:bg-[#1d4ed8] text-white rounded-full">
                <Plus className="w-4 h-4 mr-2" /> New project
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="thesis-projects-grid">
            {projects.map((p) => (
              <Card key={p.id} className="group hover:shadow-lg hover:border-[#1d4ed8]/30 transition-all"
                    data-testid={`thesis-project-card-${p.id}`}>
                <CardHeader>
                  <div className="flex items-start justify-between gap-2">
                    <CardTitle className="text-lg leading-tight font-serif"
                               style={{ fontFamily: '"Fraunces", Georgia, serif' }}>
                      {p.title}
                    </CardTitle>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {p.is_demo && (
                        <Badge className="bg-violet-600 hover:bg-violet-600 text-white text-[10px]
                          uppercase tracking-widest" data-testid={`project-demo-badge-${p.id}`}>
                          Demo
                        </Badge>
                      )}
                      <Badge variant="outline">{(p.language || 'en').toUpperCase()}</Badge>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                    <Badge variant="secondary" className="text-[10px] uppercase tracking-widest font-semibold">
                      {PROJECT_TYPE_LABEL[p.project_type] || 'Research'}
                    </Badge>
                    {p.primary_guideline_id && (
                      <Badge variant="outline" className="text-[10px] uppercase tracking-widest font-semibold border-[#1d4ed8]/40 text-[#1d4ed8] dark:text-[#7aa9ff]">
                        {p.primary_guideline_id}
                      </Badge>
                    )}
                  </div>
                  <CardDescription className="line-clamp-2 mt-2">
                    {p.description || p.research_question || 'No description yet'}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between text-sm text-muted-foreground mb-4">
                    <span>{p.document_count || 0} documents</span>
                    <span>{p.chapter_count || 0} sections</span>
                  </div>
                  <div className="flex gap-2">
                    <Button asChild size="sm" className="flex-1 bg-[#0b1a35] hover:bg-[#1d4ed8] text-white" data-testid={`thesis-open-${p.id}`}>
                      <Link to={`/thesis/${p.id}`} data-testid={`project-open-${p.id}`}>Open <ArrowRight className="w-3 h-3 ml-1" /></Link>
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(p.id, p.title)} data-testid={`thesis-delete-${p.id}`}>
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      <ProjectCreationWizard
        open={wizardOpen}
        onOpenChange={setWizardOpen}
        userId={user.id}
        onCreated={handleCreated}
      />

      <AppFooter lang="en" />
    </div>
  );
}
